import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Printer,
  Scissors,
  Palette,
  ChevronRight,
  ClipboardList,
  Phone,
  FileText,
  Layers,
  Users,
  ShieldCheck,
  Sparkles,
  Building2,
  Package,
  CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Volume price tables ----------
const SCREEN_PRICE_TABLE = [
  { tier: "24–49 units", tee: "$14.50", hoodie: "$32.50", tote: "$11.00" },
  { tier: "50–99 units", tee: "$11.75", hoodie: "$27.50", tote: "$8.50" },
  { tier: "100–249 units", tee: "$9.25", hoodie: "$22.00", tote: "$6.75" },
  { tier: "250+ units", tee: "Custom", hoodie: "Custom", tote: "Custom" },
];

const EMB_PRICE_TABLE = [
  { tier: "24–49 units", polo: "$22.00", cap: "$14.50", jacket: "$46.00" },
  { tier: "50–99 units", polo: "$18.50", cap: "$11.75", jacket: "$39.00" },
  { tier: "100–249 units", polo: "$15.75", cap: "$9.50", jacket: "$33.50" },
  { tier: "250+ units", polo: "Custom", cap: "Custom", jacket: "Custom" },
];

// ---------- Small helper components ----------
const Pill = ({ children, tone = "black" }) => {
  const cls =
    tone === "pink"
      ? "bg-[#EC008C] text-white border-[#EC008C]"
      : tone === "lime"
      ? "bg-[#DCE842] text-black border-black"
      : tone === "yellow"
      ? "bg-[#F3E30A] text-black border-black"
      : "bg-white text-black border-black";
  return (
    <span className={`inline-flex items-center gap-1.5 border-2 ${cls} px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase font-black`}>
      {children}
    </span>
  );
};

const SectionKicker = ({ children, tone = "pink" }) => (
  <div
    className={`text-[11px] tracking-[0.28em] uppercase font-black mb-4 ${
      tone === "lime" ? "text-[#DCE842]" : "text-[#EC008C]"
    }`}
  >
    {children}
  </div>
);

const ServiceBlock = ({ id, icon, label, title, blurb, bestFor, features, tint, imageLabel }) => (
  <section id={id} data-testid={`service-${id}`} className="border-t-2 border-black">
    <div className="container-x py-20 md:py-28 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        <div
          className="aspect-square border-2 border-black flex items-center justify-center relative"
          style={{ background: tint }}
        >
          {icon}
          <div className="absolute bottom-0 left-0 right-0 bg-black text-white text-[10px] tracking-[0.28em] uppercase font-black px-4 py-3">
            {imageLabel}
          </div>
        </div>
      </div>
      <div className="md:col-span-8">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">{label}</div>
        <h2 className="font-display font-black text-4xl md:text-5xl text-black leading-[1.02] tracking-tight">
          {title}
        </h2>
        <p className="mt-5 text-base md:text-lg text-[#3A3A3A] font-light leading-relaxed max-w-2xl">{blurb}</p>
        <div className="mt-6 border-l-4 border-[#EC008C] pl-5 py-2 max-w-2xl">
          <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#EC008C] mb-1">Best for</div>
          <div className="text-sm md:text-base text-black font-light">{bestFor}</div>
        </div>
        <ul className="mt-8 space-y-3 max-w-2xl">
          {features.map((f, i) => (
            <li key={i} className="flex gap-3 items-start">
              <CheckCircle2 size={18} className="text-[#EC008C] shrink-0 mt-0.5" strokeWidth={2.5} />
              <span className="text-base text-black font-light">{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// ---------- Main page ----------
const Bulk = () => {
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    service: "screen-printing",
    garment_type: "",
    quantity: "",
    print_locations: [],
    deadline: "",
    budget: "",
    notes: "",
    artwork_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLocation = (loc) =>
    setForm((f) => ({
      ...f,
      print_locations: f.print_locations.includes(loc)
        ? f.print_locations.filter((x) => x !== loc)
        : [...f.print_locations, loc],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please fill in your name, email, and pick a service.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bulk-quote`, form);
      setSubmitted(true);
      toast.success("Quote request received. We'll reply within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="bulk-page" className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#111111] text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #EC008C 0%, transparent 40%), radial-gradient(circle at 80% 70%, #DCE842 0%, transparent 40%)",
          }}
        />
        <div className="relative container-x py-24 md:py-36 max-w-6xl">
          <Pill tone="lime">
            <Building2 size={12} /> B2B · Corporate · Institutional
          </Pill>
          <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white max-w-4xl">
            Custom Apparel, <span className="text-[#DCE842]">Screen Printing</span> &amp;{" "}
            <span className="text-[#EC008C]">Embroidery</span> Solutions.
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-white/85 leading-relaxed font-light">
            Full-service bulk apparel &amp; corporate merch built by MAKEBA Design &amp; Prints. From 24-piece team runs to
            10,000-unit institutional orders — screen printed, embroidered, or fully designed from scratch.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#quote"
              data-testid="bulk-hero-cta-quote"
              className="inline-flex items-center gap-2 bg-[#EC008C] text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#EC008C] hover:bg-white hover:text-black hover:border-white transition-colors"
            >
              Request a Custom Bulk Quote <ArrowRight size={14} />
            </a>
            <a
              href="mailto:hello@makebadesigns.com?subject=15-min%20merch%20strategy%20call"
              data-testid="bulk-hero-cta-call"
              className="inline-flex items-center gap-2 bg-transparent text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-white hover:text-black transition-colors"
            >
              <Phone size={14} /> Book a 15-Min Strategy Call
            </a>
            <a
              href="#po"
              data-testid="bulk-hero-cta-po"
              className="inline-flex items-center gap-2 bg-[#DCE842] text-black px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#DCE842] hover:bg-white hover:border-white transition-colors"
            >
              <FileText size={14} /> Submit Purchase Order
            </a>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section data-testid="bulk-trust" className="bg-[#F3E30A] border-y-2 border-black">
        <div className="container-x py-8 md:py-10">
          <div className="text-center text-[11px] md:text-[13px] tracking-[0.28em] uppercase font-black text-black mb-5">
            Trusted by Corporate Teams · Schools · HBCUs · Municipalities · Non-Profits
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Pill>
              <ShieldCheck size={12} className="text-[#EC008C]" /> W-9 &amp; Net-30 Available
            </Pill>
            <Pill>
              <Package size={12} className="text-[#EC008C]" /> Nationwide Shipping
            </Pill>
            <Pill>
              <Users size={12} className="text-[#EC008C]" /> Dedicated Account Manager
            </Pill>
            <Pill>
              <Sparkles size={12} className="text-[#EC008C]" /> Digital Proof Portal
            </Pill>
          </div>
        </div>
      </section>

      {/* PAGE ARCHITECTURE / NAV JUMP */}
      <section className="container-x py-16 md:py-20">
        <SectionKicker>Three Ways We Work</SectionKicker>
        <h2 className="font-display font-black text-4xl md:text-6xl text-black leading-[1.02] tracking-tight max-w-3xl">
          Screen Printing. Embroidery. Full Custom Design.
        </h2>
        <p className="mt-6 max-w-2xl text-base md:text-lg text-[#3A3A3A] font-light leading-relaxed">
          Pick your path — every one of them ends with your logo, your team, and your brand in beautifully printed apparel.
        </p>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { id: "screen-printing", icon: <Printer size={22} strokeWidth={2.5} />, title: "Screen Printing", copy: "High-volume tees, hoodies, totes." },
            { id: "embroidery", icon: <Scissors size={22} strokeWidth={2.5} />, title: "Custom Embroidery", copy: "Polos, caps, jackets, uniforms." },
            { id: "design", icon: <Palette size={22} strokeWidth={2.5} />, title: "Creative Studio", copy: "Design + production, done-for-you." },
          ].map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              data-testid={`jump-${c.id}`}
              className="group border-2 border-black p-6 md:p-7 flex items-center gap-5 hover:bg-black hover:text-white transition-colors"
            >
              <div className="w-14 h-14 rounded-full bg-[#DCE842] border-2 border-black flex items-center justify-center text-black shrink-0">
                {c.icon}
              </div>
              <div className="flex-1">
                <div className="font-display font-black text-xl leading-tight">{c.title}</div>
                <div className="text-sm mt-1 opacity-70 font-light">{c.copy}</div>
              </div>
              <ChevronRight size={18} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </a>
          ))}
        </div>
      </section>

      {/* SERVICE A — SCREEN PRINTING */}
      <ServiceBlock
        id="screen-printing"
        icon={<Printer size={80} strokeWidth={1.5} className="text-black" />}
        label="Service A"
        title="Custom Screen Printing."
        blurb="Vivid, high-durability plastisol and water-based screen printing on tees, hoodies, and totes. Ideal for event giveaways, fundraising merch, campus drops, and Fortune-500 team runs."
        bestFor="High-volume t-shirts, hoodies, tote bags, event giveaways, and fundraising merch."
        features={[
          "Front, back, sleeve, and neck-tag print positions",
          "1–8 colour prints with Pantone matching",
          "Water-based inks for premium soft-hand finish",
          "Free print/fabric swatch sample kit for corporate leads (250+ units)",
          "Rush turnaround as fast as 5 business days on requalifying orders",
        ]}
        tint="#DCE842"
        imageLabel="1–8 Colour Screens"
      />

      {/* Screen printing price table */}
      <section className="container-x pb-16">
        <div className="border-2 border-black overflow-hidden">
          <div className="bg-black text-white px-6 py-4 flex items-center justify-between">
            <div className="font-display font-black text-lg">Screen Print · Volume Price Breaks</div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#DCE842]">1-Colour Front · Standard Garments</div>
          </div>
          <div className="grid grid-cols-4 text-sm">
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">Quantity</div>
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">T-Shirt</div>
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">Hoodie</div>
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">Tote Bag</div>
            {SCREEN_PRICE_TABLE.map((r, i) => (
              <React.Fragment key={i}>
                <div className={`p-4 font-display font-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.tier}</div>
                <div className={`p-4 text-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.tee}</div>
                <div className={`p-4 text-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.hoodie}</div>
                <div className={`p-4 text-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.tote}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="bg-[#FAFAF4] border-t border-black/20 px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-black text-[#EC008C]">
            Additional print colours from $1.25 / unit · Contact us for 250+ &amp; custom Pantone quotes.
          </div>
        </div>
      </section>

      {/* SERVICE B — EMBROIDERY */}
      <ServiceBlock
        id="embroidery"
        icon={<Scissors size={80} strokeWidth={1.5} className="text-white" />}
        label="Service B"
        title="Custom Embroidery."
        blurb="Durable, high-thread-count embroidery for the pieces employees actually wear — polos, jackets, hats, and uniforms. We digitize your logo, match your brand thread, and stitch it clean."
        bestFor="Corporate outerwear, polos, hats, jackets, workwear, and Greek-life apparel."
        features={[
          "Free logo digitizing on orders of 50+ units",
          "Pantone thread matching for exact brand colours",
          "High-durability threads suited for industrial uniforms",
          "3D puff, tone-on-tone, and metallic thread options",
          "Repeat-order files saved in your account for one-click reruns",
        ]}
        tint="#EC008C"
        imageLabel="Digitized · Stitched · Ready"
      />

      {/* Embroidery price table */}
      <section className="container-x pb-16">
        <div className="border-2 border-black overflow-hidden">
          <div className="bg-black text-white px-6 py-4 flex items-center justify-between">
            <div className="font-display font-black text-lg">Embroidery · Volume Price Breaks</div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#DCE842]">Left-Chest Logo · Up to 8k Stitches</div>
          </div>
          <div className="grid grid-cols-4 text-sm">
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">Quantity</div>
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">Polo</div>
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">Cap</div>
            <div className="bg-[#111] text-[#DCE842] p-4 text-[10px] tracking-[0.28em] uppercase font-black">Jacket</div>
            {EMB_PRICE_TABLE.map((r, i) => (
              <React.Fragment key={i}>
                <div className={`p-4 font-display font-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.tier}</div>
                <div className={`p-4 text-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.polo}</div>
                <div className={`p-4 text-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.cap}</div>
                <div className={`p-4 text-black ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF4]"}`}>{r.jacket}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="bg-[#FAFAF4] border-t border-black/20 px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-black text-[#EC008C]">
            Additional locations, 3D puff, or 8k+ stitches priced on quote.
          </div>
        </div>
      </section>

      {/* SERVICE C — DESIGN */}
      <ServiceBlock
        id="design"
        icon={<Palette size={80} strokeWidth={1.5} className="text-black" />}
        label="Service C"
        title="Apparel &amp; Merchandise Design Studio."
        blurb="Not sure what you want printed yet? We design your merch from scratch, deliver polished digital proofs, and roll it straight into production so you receive finished apparel — not just an artwork file."
        bestFor="Brand launches, custom merch lines, event drops, and exclusive artwork commissions."
        features={[
          "Design-to-Print packages (e.g. \"Full merch design + first 50 shirts printed\")",
          "Tech pack &amp; mockup approvals inside your client portal",
          "Two revision rounds included, unlimited on retainer",
          "Original illustration &amp; hand-lettering by MAKEBA studio",
          "Optional exclusive-artwork buy-out for permanent brand ownership",
        ]}
        tint="#F3E30A"
        imageLabel="Concept → Proof → Print"
      />

      {/* HIGH-CONVERTING FEATURE GRID */}
      <section data-testid="bulk-features" className="bg-[#0E0E0E] text-white border-y-2 border-black py-24 md:py-32">
        <div className="container-x">
          <SectionKicker tone="lime">Built for B2B Sales</SectionKicker>
          <h2 className="font-display font-black text-4xl md:text-6xl text-white leading-[1.02] tracking-tight max-w-3xl">
            The tools that turn a query into a purchase order.
          </h2>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: <ClipboardList size={26} strokeWidth={2.5} />,
                title: "Interactive Quote Form",
                copy: "Pick garment, colour, artwork locations, and print vs stitch — get a same-day quote in your inbox.",
              },
              {
                icon: <Layers size={26} strokeWidth={2.5} />,
                title: "Digital Proof Portal",
                copy: "Approve 2D &amp; 3D mockups before we hit the press. No costly misprints, no rework.",
              },
              {
                icon: <Package size={26} strokeWidth={2.5} />,
                title: "Sample Kit Ordering",
                copy: "Order a paid sample kit of prints &amp; embroidery patches — credited toward your first full order.",
              },
              {
                icon: <Users size={26} strokeWidth={2.5} />,
                title: "Re-Order Portal",
                copy: "Saved artwork &amp; specs. Returning clients re-order team uniforms or merch in two clicks.",
              },
            ].map((f, i) => (
              <div key={i} className="border-2 border-[#DCE842] p-6 md:p-7 bg-black hover:bg-[#111] transition-colors">
                <div className="w-12 h-12 rounded-full border-2 border-[#DCE842] flex items-center justify-center text-[#DCE842] mb-5">
                  {f.icon}
                </div>
                <div className="font-display font-black text-xl leading-tight text-white">{f.title}</div>
                <p className="mt-3 text-sm text-white/70 leading-relaxed font-light">{f.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" data-testid="bulk-quote-section" className="container-x py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <SectionKicker>Get a Quote</SectionKicker>
            <h2 className="font-display font-black text-4xl md:text-5xl text-black leading-[1.02] tracking-tight">
              Same-day response. Custom pricing.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#3A3A3A] font-light leading-relaxed">
              Tell us what you need and we&rsquo;ll email a quote within one business day — with garment options, pricing tiers,
              and a proposed timeline.
            </p>
            <div className="mt-8 space-y-4 border-t border-black/10 pt-8">
              <div className="flex gap-3 items-start">
                <Phone size={18} className="text-[#EC008C] shrink-0 mt-0.5" strokeWidth={2.5} />
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-1">Talk to a human</div>
                  <a href="mailto:hello@makebadesigns.com" className="text-base text-black font-light hover:text-[#EC008C]">
                    hello@makebadesigns.com
                  </a>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <FileText size={18} className="text-[#EC008C] shrink-0 mt-0.5" strokeWidth={2.5} />
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-1">Purchase orders</div>
                  <div className="text-base text-black font-light">W-9 &amp; Net-30 accepted for schools, districts, municipalities.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-8">
            {submitted ? (
              <div
                data-testid="bulk-quote-thanks"
                className="border-2 border-[#EC008C] bg-[#EC008C] text-white p-10 md:p-14 text-center"
              >
                <CheckCircle2 size={40} className="mx-auto mb-5" strokeWidth={2.5} />
                <div className="font-display font-black text-3xl md:text-4xl leading-tight">
                  Quote request received.
                </div>
                <p className="mt-4 text-white/90 font-light text-base md:text-lg max-w-xl mx-auto">
                  Our merch team will reply within one business day with pricing tiers, a proposed timeline, and next steps.
                </p>
              </div>
            ) : (
              <form
                data-testid="bulk-quote-form"
                onSubmit={submit}
                className="border-2 border-black p-6 md:p-10 bg-[#FAFAF4]"
              >
                {/* Service selector */}
                <div className="mb-6">
                  <label className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-3 block">
                    What service do you need?
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { v: "screen-printing", l: "Screen Print" },
                      { v: "embroidery", l: "Embroidery" },
                      { v: "design", l: "Design + Print" },
                      { v: "strategy-call", l: "Strategy Call" },
                    ].map((o) => (
                      <button
                        type="button"
                        key={o.v}
                        onClick={() => update("service", o.v)}
                        data-testid={`bulk-service-${o.v}`}
                        className={`border-2 border-black px-3 py-3 text-[11px] tracking-[0.24em] uppercase font-black transition-colors ${
                          form.service === o.v
                            ? "bg-[#EC008C] text-white border-[#EC008C]"
                            : "bg-white text-black hover:bg-[#F3E30A]"
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Company / Organization" value={form.company} onChange={(v) => update("company", v)} testId="bulk-field-company" />
                  <Field required label="Your Name" value={form.name} onChange={(v) => update("name", v)} testId="bulk-field-name" />
                  <Field required type="email" label="Work Email" value={form.email} onChange={(v) => update("email", v)} testId="bulk-field-email" />
                  <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testId="bulk-field-phone" />
                  <Field label="Garment Type (e.g. Tee, Polo, Hoodie)" value={form.garment_type} onChange={(v) => update("garment_type", v)} testId="bulk-field-garment" />
                  <Field label="Estimated Quantity" value={form.quantity} onChange={(v) => update("quantity", v)} testId="bulk-field-qty" />
                  <Field label="Target Deadline" value={form.deadline} onChange={(v) => update("deadline", v)} testId="bulk-field-deadline" />
                  <Field label="Budget Range (optional)" value={form.budget} onChange={(v) => update("budget", v)} testId="bulk-field-budget" />
                </div>

                {/* Print locations */}
                <div className="mt-6">
                  <label className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-3 block">
                    Print / Stitch Locations
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["Front", "Back", "Left Chest", "Right Chest", "Left Sleeve", "Right Sleeve", "Neck Tag", "Cap Front"].map((loc) => (
                      <button
                        type="button"
                        key={loc}
                        onClick={() => toggleLocation(loc)}
                        data-testid={`bulk-loc-${loc.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`border-2 border-black px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-black transition-colors ${
                          form.print_locations.includes(loc)
                            ? "bg-black text-[#DCE842] border-black"
                            : "bg-white text-black hover:bg-[#F3E30A]"
                        }`}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-6">
                  <label className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-2 block">
                    Notes / Project Brief
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    data-testid="bulk-field-notes"
                    rows={4}
                    placeholder="Tell us about the campaign, audience, and any brand guidelines we should follow."
                    className="w-full border-2 border-black px-4 py-3 bg-white text-black text-sm font-light focus:outline-none focus:border-[#EC008C]"
                  />
                </div>

                {/* Artwork URL */}
                <div className="mt-6">
                  <label className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-2 block">
                    Artwork Link (Google Drive, Dropbox, etc.)
                  </label>
                  <input
                    type="url"
                    value={form.artwork_url}
                    onChange={(e) => update("artwork_url", e.target.value)}
                    data-testid="bulk-field-artwork"
                    placeholder="https://drive.google.com/..."
                    className="w-full border-2 border-black px-4 py-3 bg-white text-black text-sm font-light focus:outline-none focus:border-[#EC008C]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="bulk-quote-submit"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#EC008C] text-white px-6 py-4 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#EC008C] hover:bg-black hover:border-black transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending..." : (<>Request My Bulk Quote <ArrowRight size={14} /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* PURCHASE ORDER STRIP */}
      <section id="po" data-testid="bulk-po" className="bg-[#EC008C] text-white border-y-2 border-black">
        <div className="container-x py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <FileText size={32} className="mb-5 text-[#F3E30A]" strokeWidth={2.5} />
            <h2 className="font-display font-black text-3xl md:text-5xl text-white leading-[1.05]">
              Institutional purchase orders — schools, districts &amp; municipalities.
            </h2>
            <p className="mt-6 text-white/90 font-light text-base md:text-lg max-w-2xl leading-relaxed">
              Send us your PO along with garment specs and delivery address. We provide a W-9, insurance certificate, and quote
              upfront and invoice per your Net-30 (or standard) terms.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a
              href="mailto:hello@makebadesigns.com?subject=Purchase%20Order%20Submission"
              data-testid="bulk-po-cta"
              className="inline-flex items-center gap-2 bg-[#F3E30A] text-black px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#F3E30A] hover:bg-white hover:border-white transition-colors"
            >
              Submit PO by Email <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA FOOTER STRIP */}
      <section className="bg-[#DCE842] py-14 md:py-16 border-t-2 border-black">
        <div className="container-x text-center max-w-4xl mx-auto">
          <div className="font-display font-black text-2xl md:text-3xl text-black leading-tight">
            MAKEBA Design &amp; Prints — Full-service custom apparel &amp; corporate merch.
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-3 text-[11px] tracking-[0.24em] uppercase font-black text-black">
            <Link to="/about" className="hover:text-[#EC008C] transition-colors">Our Story</Link>
            <span className="opacity-30">·</span>
            <Link to="/shop" className="hover:text-[#EC008C] transition-colors">Retail Shop</Link>
            <span className="opacity-30">·</span>
            <Link to="/steam" className="hover:text-[#EC008C] transition-colors">STEAM Kits</Link>
            <span className="opacity-30">·</span>
            <Link to="/contact" className="hover:text-[#EC008C] transition-colors">Contact</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, value, onChange, testId, required, type = "text" }) => (
  <div>
    <label className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-2 block">
      {label} {required && <span className="text-[#EC008C]">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      required={required}
      className="w-full border-2 border-black px-4 py-3 bg-white text-black text-sm font-light focus:outline-none focus:border-[#EC008C]"
    />
  </div>
);

export default Bulk;
